/**
 * @module services/type-transformers
 * @description 图表类型特有字段转换器 — convert() S5 步骤调用
 *
 * 数据流：
 *   convert() S4 过滤后 → S5 applyTypeTransform(chartType, values) → 返回转换后的值
 *
 * 关键：
 * - 大多数图表类型无需特殊转换（identityTransformer）
 * - StackBar / PercentStackBar 需要 TopN 字段重命名（C4 兼容）
 * - Table 补齐 column_sort 项缺失的 table_code（默认 t0）
 * - MixedChart 合并主轴/次轴配置
 *
 * 兼容处理：
 * - C4：StackBar TopN 字段从通用名改为 xAxis 前缀
 *
 * @see PRD 附录-配置项数据结构向后兼容分析 — C4
 * @see 渲染方案 §六 — 图表类型特有转换
 */

import { ChartType, type TChartType } from '@dbl/core/chart';
import type { IColumnSortItem } from '@/pages/datalab/pages/Multidimensional/Analysis/types/chartSettings/fieldColumnSort';
import { formatMixedChartForSubmit } from '../settingGroups/common/DualAxis/axisMetricUtils';

/**
 * 类型转换器函数签名
 * @param values - S4 过滤后的配置值
 * @returns 转换后的配置值
 */
type TypeTransformer = (values: Record<string, unknown>) => Record<string, unknown>;

const DEFAULT_COLUMN_SORT_TABLE_CODE = 't0';

function resolveColumnSortTableCode(tableCode: string | undefined): string {
  if (tableCode) {
    const trimmed = tableCode.trim();
    if (trimmed) {
      return trimmed;
    }
  }
  return DEFAULT_COLUMN_SORT_TABLE_CODE;
}

/**
 * 上传前补齐 column_sort 各元素的 table_code（自定义字段等场景可能缺失）
 */
function normalizeColumnSortForUpload(
  columnSort: IColumnSortItem[],
): IColumnSortItem[] {
  return columnSort.map((item: IColumnSortItem) => {
    const tableCode = resolveColumnSortTableCode(item.table_code);
    if (item.table_code === tableCode) {
      return item;
    }

    return {
      ...item,
      table_code: tableCode,
    };
  });
}

/** 表格：column_sort table_code 兜底为 t0 */
function tableChartTransformer(
  values: Record<string, unknown>,
): Record<string, unknown> {
  // 没有上传column_sort时, 不需要处理
  if (!('column_sort' in values)) {
    return values;
  }
  if (!Array.isArray(values.column_sort)) {
    // 目前前端逻辑不会出现这种情况.
    return {
      ...values,
      column_sort: [],
    };
  }

  return {
    ...values,
    column_sort: normalizeColumnSortForUpload(
      values.column_sort as IColumnSortItem[],
    ),
  };
}

/**
 * StackBar / PercentStackBar 转换器
 *
 * 字段重命名：通用 TopN 字段 → xAxis 前缀
 *   dynamicState → xAxisDynamicState
 *   count        → xAxisCount
 *   showOthers   → xAxisShowOthers
 *
 * 堆叠图有两套 TopN：X轴TopN（控制数据条数）和堆叠层TopN（控制层数）
 * 上传时需区分两套字段名，避免覆盖
 */
function stackBarTransformer(values: Record<string, unknown>): Record<string, unknown> {
  const { dynamicState, count, showOthers, ...rest } = values;
  const result: Record<string, unknown> = { ...rest };
  // 仅当字段存在时才赋值，保留 undefined 语义
  if (dynamicState !== undefined) result.xAxisDynamicState = dynamicState;
  if (count !== undefined) result.xAxisCount = count;
  if (showOthers !== undefined) result.xAxisShowOthers = showOthers;
  return result;
}

/**
 * 直通转换器 — 大多数图表类型不修改字段结构
 */
function identityTransformer(values: Record<string, unknown>): Record<string, unknown> {
  return values;
}

/** 折线双轴：对齐 ChartsDrop lineChartHandler 提交结构 */
function lineChartTransformer(values: Record<string, unknown>): Record<string, unknown> {
  const result = { ...values };
  const dualYAxisEnabled = !!result.dualYAxisEnabled;
  if (!dualYAxisEnabled) {
    return result;
  }

  const formatLineAxis = (axis: unknown): Record<string, unknown> => {
    const raw = (axis || {}) as Record<string, unknown>;
    const metrics = Array.isArray(raw.metrics) ? raw.metrics : [];
    return {
      yAxisName: typeof raw.yAxisName === 'string' ? raw.yAxisName.trim() : '',
      chartType: 'line',
      metrics,
      yAxisRangeSwitch: !!raw.yAxisRangeSwitch,
    };
  };

  result.primaryYAxis = formatLineAxis(result.primaryYAxis);
  result.secondYAxis = formatLineAxis(result.secondYAxis);
  result.yAxisName = '';
  return result;
}

/** 组合图：仅 primaryYAxis + secondYAxis 对象（与折线双轴顶层结构一致，无顶层 chartType） */
function mixedChartTransformer(values: Record<string, unknown>): Record<string, unknown> {
  return formatMixedChartForSubmit(values);
}

/** 图表类型 → 转换器映射表（仅注册有特殊处理的类型，其余用 identityTransformer） */
const TYPE_TRANSFORMERS: Partial<Record<TChartType, TypeTransformer>> = {
  [ChartType.StackBar]: stackBarTransformer,
  [ChartType.PercentStackBar]: stackBarTransformer,
  [ChartType.Line]: lineChartTransformer,
  [ChartType.EventLine]: lineChartTransformer,
  [ChartType.MixedChart]: mixedChartTransformer,
  [ChartType.Table]: tableChartTransformer,
};

/**
 * 对 S4 过滤后的值应用图表类型特有转换
 *
 * @param chartType - 当前图表类型
 * @param values - S4 filterFormValues 过滤后的配置值
 * @returns 转换后的值（无转换类型返回原值）
 */
export function applyTypeTransform(
  chartType: TChartType,
  values: Record<string, unknown>,
): Record<string, unknown> {
  const transformer = TYPE_TRANSFORMERS[chartType];
  if (!transformer) return values;
  return transformer(values);
}

